import { useState } from "react";
import AppShell from "@/components/layout/AppShell";
import useAutoRefresh from "@/hooks/useAutoRefresh";
import PhotoUpload from "@/components/common/PhotoUpload";
import StatusBadge from "@/components/common/StatusBadge";
import DataTable from "@/components/common/DataTable";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const initial = {
  opname_date: new Date().toISOString().slice(0,10),
  tank_id: "", tank_name: "", product: "",
  petugas: "", supervisor: "",
  method: "dipping", measured_qty: 0, unit: "L",
  temperature: "", notes: "", book_stock: 0,
  photo_tool: "", photo_point: "", photo_reading: "", photo_tank: "",
};

const METHODS = [
  { v: "dipping",   l: "Dipping" },
  { v: "flowmeter", l: "Flow Meter" },
  { v: "sounding",  l: "Sounding" },
];

const PRODUCT_OPTIONS = ["Presol", "Setengah Jadi", "Minyak Bakar", "Campuran", "Soda Api", "Oli Bekas", "Solar Kotor"];

export default function StockOpnamePage() {
  const [form, setForm] = useState(initial);
  const [tanks, setTanks] = useState([]);
  const [supporting, setSupporting] = useState([]);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const load = () => {
    api.get("/master/tanks").then((r) => setTanks(r.data)).catch(()=>{});
    api.get("/master/supporting-materials").then((r) => setSupporting(r.data)).catch(()=>{});
    api.get("/opname").then((r) => setRows(r.data)).catch(()=>{});
  };
  useAutoRefresh(load);

  const selectedIsStorageTank = tanks.some((t) => t.id === form.tank_id);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        measured_qty: parseFloat(form.measured_qty) || 0,
        book_stock: parseFloat(form.book_stock) || 0,
        temperature: form.temperature ? parseFloat(form.temperature) : null,
      };
      const { data } = await api.post("/opname", payload);
      if (data?.tank_synced) {
        toast.success("Opname tersimpan · Stok tangki real-time ikut diperbarui");
      } else {
        toast.success("Stok opname harian tersimpan");
      }
      setForm({ ...initial, opname_date: form.opname_date });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <AppShell title="Stok Opname Harian" subtitle="Input hasil pengukuran fisik tangki. Foto wajib pada 4 titik.">
      <form onSubmit={submit} data-testid="opname-form" className="rounded-xl bg-white border border-slate-200 p-6 shadow-sm space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Tanggal Opname" required>
            <Input data-testid="opname-date" type="date" value={form.opname_date} onChange={set("opname_date")} required />
          </Field>
          <Field label="Tank / Stock Point" required>
            <Select value={form.tank_id} onValueChange={(id) => {
              const list = [...tanks, ...supporting];
              const t = list.find((x) => x.id === id);
              const isTank = tanks.some((x) => x.id === id);
              setForm((f) => ({
                ...f, tank_id: id,
                tank_name: t?.name || t?.code || "",
                // Prefill product only if it's a valid dropdown option (storage tank)
                product: isTank && PRODUCT_OPTIONS.includes(t?.product) ? t.product : (isTank ? "" : (t?.name || "")),
                // For storage tanks use current_stock as book reference; supporting materials use book_stock
                book_stock: isTank ? (t?.current_stock || 0) : (t?.book_stock || 0),
                unit: t?.unit || "L",
              }));
            }}>
              <SelectTrigger data-testid="opname-tank" className="h-10"><SelectValue placeholder="Pilih titik ukur" /></SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">Storage Tank</div>
                {tanks.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                <div className="px-2 py-1 mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-500 border-t border-slate-100">Bahan Pendukung</div>
                {supporting.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} ({s.unit})</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          {selectedIsStorageTank ? (
            <Field label="Jenis Barang di Tangki" required>
              <Select value={form.product} onValueChange={set("product")}>
                <SelectTrigger data-testid="opname-product" className="h-10">
                  <SelectValue placeholder="Pilih jenis minyak" />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          ) : (
            <Field label="Jenis Barang">
              <Input data-testid="opname-product-text" value={form.product} onChange={set("product")} disabled placeholder="—" />
            </Field>
          )}
          <Field label="Metode Ukur" required>
            <Select value={form.method} onValueChange={set("method")}>
              <SelectTrigger data-testid="opname-method" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>{METHODS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Petugas Opname" required>
            <Input data-testid="opname-petugas" value={form.petugas} onChange={set("petugas")} required />
          </Field>
          <Field label="Supervisor Verifikasi">
            <Input data-testid="opname-supervisor" value={form.supervisor} onChange={set("supervisor")} />
          </Field>
          <Field label="Temperatur (°C)">
            <Input data-testid="opname-temp" type="number" step="0.1" value={form.temperature} onChange={set("temperature")} className="font-mono" />
          </Field>
          <Field label="Hasil Ukur Fisik" required>
            <Input data-testid="opname-qty" type="number" step="0.01" value={form.measured_qty} onChange={set("measured_qty")} className="font-mono" required />
          </Field>
          <Field label="Satuan"><Input data-testid="opname-unit" value={form.unit} onChange={set("unit")} /></Field>
          <Field label="Stok Buku / Stok Tangki Saat Ini">
            <Input data-testid="opname-book" type="number" step="0.01" value={form.book_stock} onChange={set("book_stock")} className="font-mono" />
          </Field>
        </div>

        {selectedIsStorageTank && (
          <div className="rounded-md bg-sky-50 border-l-4 border-sky-500 p-3 text-[12px] text-sky-900">
            <b>Sinkron otomatis:</b> Saat opname ini disubmit, <b>Stok Tangki Real-time</b> untuk tangki tersebut akan diperbarui dengan Hasil Ukur Fisik. Jenis barang tangki juga ikut diupdate. Perubahan tercatat di Riwayat Overwrite.
          </div>
        )}

        <Field label="Catatan Kondisi">
          <Textarea data-testid="opname-notes" rows={2} value={form.notes} onChange={set("notes")} />
        </Field>

        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800">Bukti Foto Wajib</h3>
            <span className="text-[10px] text-red-600 font-mono uppercase tracking-wider">4 foto diperlukan</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <PhotoUpload label="Alat Ukur"       required value={form.photo_tool}    onChange={set("photo_tool")}    testid="opname-photo-tool" />
            <PhotoUpload label="Titik Ukur"      required value={form.photo_point}   onChange={set("photo_point")}   testid="opname-photo-point" />
            <PhotoUpload label="Hasil Pembacaan" required value={form.photo_reading} onChange={set("photo_reading")} testid="opname-photo-reading" />
            <PhotoUpload label="Kondisi Tangki"  required value={form.photo_tank}    onChange={set("photo_tank")}    testid="opname-photo-tank" />
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" data-testid="opname-submit" disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white h-11 px-6">
            {submitting ? "Memproses…" : "Submit Opname"}
          </Button>
        </div>
      </form>

      <div className="mt-8">
        <h3 className="font-heading text-sm font-bold uppercase tracking-wider text-slate-800 mb-3">Hasil Opname Terbaru</h3>
        <DataTable
          testid="opname-table"
          rows={rows}
          columns={[
            { key: "opname_date", label: "Tanggal", render: (r) => <span className="font-mono">{r.opname_date}</span> },
            { key: "tank_name", label: "Tank" },
            { key: "product", label: "Produk" },
            { key: "measured_qty", label: "Hasil Ukur", render: (r) => <span className="font-mono">{r.measured_qty?.toLocaleString("id-ID")}</span> },
            { key: "book_stock", label: "Stok Buku", render: (r) => <span className="font-mono">{r.book_stock?.toLocaleString("id-ID")}</span> },
            { key: "variance", label: "Selisih", render: (r) => (
              <span className={`font-mono font-bold ${r.variance < 0 ? "text-red-600" : "text-emerald-600"}`}>
                {r.variance > 0 ? "+" : ""}{r.variance?.toLocaleString("id-ID")}
              </span>
            ) },
            { key: "variance_pct", label: "%", render: (r) => <span className="font-mono">{r.variance_pct?.toFixed(2)}%</span> },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]}
        />
      </div>
    </AppShell>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-600">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
